import { useEffect, useState } from 'react';
import { Skeleton, useAsync, useToast } from '../../components/ui';
import { JourneyCanvas, Mark } from '../../journeys/JourneyCanvas';
import { APPROVAL_STATUS, Graph, GraphDiff, JourneyDetail, shortHash, SOURCE_LABEL, VersionRow } from '../../journeys/model';
import { api } from '../../lib/api';
import { dateTime, num } from '../../lib/format';
import { t } from '../../lib/i18n';
import { errorText } from '../Contacts';

const fmt = (v: unknown) => (v === null || v === undefined ? '—' : typeof v === 'string' ? v : JSON.stringify(v));

/**
 * Versions and comparison (docs/journeys/SPEC.md §4.1): every immutable version with its hash, source and approval;
 * the diff by steps highlighted on the canvas. Owners can move in-flight enrollments to the active version.
 */
export function JourneyDiff({ journey, versions, owner, onChanged }: { journey: JourneyDetail; versions: VersionRow[]; owner: boolean; onChanged: () => void }) {
  const latest = journey.latest_version;
  const [to, setTo] = useState(latest);
  const [from, setFrom] = useState(journey.active_version && journey.active_version !== latest ? journey.active_version : Math.max(0, latest - 1));
  useEffect(() => {
    setTo(latest);
    setFrom(journey.active_version && journey.active_version !== latest ? journey.active_version : Math.max(0, latest - 1));
  }, [latest, journey.active_version]);
  const diff = useAsync(() => (to ? api.get<GraphDiff>(`/journeys/${journey.id}/diff?from=${from}&to=${to}`) : Promise.resolve(null)), [journey.id, from, to]);
  const graph = useAsync(() => (to ? api.get<{ graph: Graph }>(`/journeys/${journey.id}/versions/${to}`) : Promise.resolve(null)), [journey.id, to]);
  const marks: Record<string, Mark> = {};
  for (const a of diff.data?.added ?? []) marks[a] = 'added';
  for (const c of diff.data?.changed ?? []) if (c.step && !marks[c.step]) marks[c.step] = 'changed';
  const byStep = new Map<string, GraphDiff['changed']>();
  for (const c of diff.data?.changed ?? []) byStep.set(c.step ?? '', [...(byStep.get(c.step ?? '') ?? []), c]);

  return (
    <div className="stack">
      <div className="card">
        <div className="card-head"><h3>{t('Версии')}</h3><span className="small muted">{t('версия неизменяема; правка графа — новая версия')}</span></div>
        <div className="table-wrap">
          <table className="table">
            <thead><tr><th>{t('Версия')}</th><th>{t('Хеш')}</th><th>{t('Источник')}</th><th>{t('Согласование')}</th><th>{t('Создана')}</th></tr></thead>
            <tbody>
              {versions.map((v) => (
                <tr key={v.version} className="click" onClick={() => { setTo(v.version); setFrom(Math.max(0, v.version - 1)); }} style={v.version === to ? { background: 'var(--surface-3)' } : undefined}>
                  <td>
                    <b>v{v.version}</b>
                    {v.version === journey.active_version && <span className={`pill ${journey.status === 'active' ? 'ok' : 'warn'}`} style={{ marginLeft: 6 }}>{journey.status === 'active' ? t('работает') : journey.status === 'stopped' ? t('остановлена') : t('на паузе')}</span>}
                  </td>
                  <td><code className="small">{shortHash(v.hash)}</code></td>
                  <td className="small">{t(SOURCE_LABEL[v.source] ?? v.source)}</td>
                  <td>{v.approval ? <span className={`pill ${APPROVAL_STATUS[v.approval.status]?.tone ?? ''}`}>{t(APPROVAL_STATUS[v.approval.status]?.label ?? v.approval.status)}</span> : <span className="small muted">—</span>}</td>
                  <td className="small muted">{dateTime(v.created_at)}{v.published_at ? ` · ${t('опубликована')}` : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card card-pad stack">
        <div className="row wrap">
          <h3 style={{ margin: 0 }}>{t('Сравнение')}</h3>
          <span className="spacer" />
          <select className="select input" style={{ width: 'auto' }} value={from} onChange={(e) => setFrom(Number(e.target.value))} aria-label={t('С версии')}>
            <option value={0}>{t('пусто')}</option>
            {versions.map((v) => <option key={v.version} value={v.version}>v{v.version}</option>)}
          </select>
          <span className="small muted">→</span>
          <select className="select input" style={{ width: 'auto' }} value={to} onChange={(e) => setTo(Number(e.target.value))} aria-label={t('На версию')}>
            {versions.map((v) => <option key={v.version} value={v.version}>v{v.version}</option>)}
          </select>
        </div>
        {diff.loading && !diff.data ? (
          <Skeleton h={80} />
        ) : diff.error ? (
          <div className="callout bad">{errorText(diff.error)}</div>
        ) : diff.data && (
          <div className="stack" style={{ gap: 8 }}>
            <div className="row wrap small" style={{ gap: 12 }}>
              <span>{t('добавлено:')} <b style={{ color: '#0f9a74' }}>{diff.data.added.join(', ') || '—'}</b></span>
              <span>{t('удалено:')} <b style={{ color: '#d9433d' }}>{diff.data.removed.join(', ') || '—'}</b></span>
              <span>{t('изменено шагов:')} <b>{byStep.size}</b></span>
            </div>
            {[...byStep.entries()].map(([step, changes]) => (
              <details key={step} className="small">
                <summary style={{ cursor: 'pointer' }}><code>{step}</code> — {changes.length === 1 ? t('{n} изменение', { n: changes.length }) : t('{n} изменений', { n: changes.length })}</summary>
                <ul style={{ margin: '6px 0', paddingLeft: 18 }}>{changes.map((c, i) => <li key={i} style={{ overflowWrap: 'anywhere' }}><code>{c.path}</code>: {fmt(c.from)} → <b>{fmt(c.to)}</b></li>)}</ul>
              </details>
            ))}
            {diff.data.top_level.length > 0 && (
              <details className="small" open>
                <summary style={{ cursor: 'pointer' }}>{t('Правила сценария')}</summary>
                <ul style={{ margin: '6px 0', paddingLeft: 18 }}>{diff.data.top_level.map((c, i) => <li key={i} style={{ overflowWrap: 'anywhere' }}><code>{c.path}</code>: {fmt(c.from)} → <b>{fmt(c.to)}</b></li>)}</ul>
              </details>
            )}
          </div>
        )}
        {graph.data && <JourneyCanvas graph={graph.data.graph} marks={marks} removed={diff.data?.removed} />}
      </div>

      {owner && journey.active_version && versions.some((v) => v.version !== journey.active_version) && (
        <Migration journey={journey} versions={versions} onDone={onChanged} />
      )}
    </div>
  );
}

function Migration({ journey, versions, onDone }: { journey: JourneyDetail; versions: VersionRow[]; onDone: () => void }) {
  const toast = useToast();
  const toVersion = journey.active_version!;
  const others = versions.filter((v) => v.version !== toVersion);
  const [fromVersion, setFromVersion] = useState(others[0]?.version ?? 0);
  const [plan, setPlan] = useState<{ by_step: Record<string, { count: number; mapped_to: string | null }> } | null>(null);
  const [busy, setBusy] = useState(false);
  const fromGraph = useAsync(() => (fromVersion ? api.get<{ graph: Graph }>(`/journeys/${journey.id}/versions/${fromVersion}`) : Promise.resolve(null)), [journey.id, fromVersion]);
  const toGraph = journey.active?.graph;
  // Same id and same type maps by default; the rest stays on its version.
  const mapping: Record<string, string> = {};
  for (const [id, s] of Object.entries(fromGraph.data?.graph.steps ?? {})) if (toGraph?.steps[id]?.type === s.type) mapping[id] = id;

  async function run(dry: boolean) {
    setBusy(true);
    try {
      const r = await api.post<{ by_step?: Record<string, { count: number; mapped_to: string | null }>; counts?: { moved: number; stayed: number } }>(`/journeys/${journey.id}/migrations`, { from_version: fromVersion, to_version: toVersion, mapping, dry_run: dry });
      if (dry) setPlan(r.by_step ? { by_step: r.by_step } : null);
      else {
        toast(t('Перенесено участий: {n}', { n: num(r.counts?.moved ?? 0) }));
        setPlan(null);
        onDone();
      }
    } catch (err) {
      toast(errorText(err), 'bad');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card card-pad stack">
      <h3 style={{ margin: 0 }}>{t('Перенести участия на работающую версию v{v}', { v: toVersion })}</h3>
      <p className="small muted" style={{ margin: 0 }}>{t('По умолчанию участия доходят по своей версии. Перенос сопоставляет шаги с тем же id и типом; уже выполненные шаги с письмами второй раз не исполняются.')}</p>
      <div className="row wrap">
        <label className="small">{t('С версии')}
          <select className="select input" style={{ marginLeft: 6, width: 'auto' }} value={fromVersion} onChange={(e) => { setFromVersion(Number(e.target.value)); setPlan(null); }}>
            {others.map((v) => <option key={v.version} value={v.version}>v{v.version}</option>)}
          </select>
        </label>
        <span className="small">{t('сопоставлено шагов:')} <b>{Object.keys(mapping).length}</b></span>
        <button className="btn btn-sm" disabled={busy || !fromVersion} onClick={() => run(true)}>{t('Посчитать (ничего не меняет)')}</button>
        {plan && <button className="btn btn-sm btn-primary" disabled={busy} onClick={() => confirm(t('Перенести участия?')) && run(false)}>{t('Перенести')}</button>}
      </div>
      {plan && (
        <table className="table">
          <thead><tr><th>{t('Шаг v{v}', { v: fromVersion })}</th><th>{t('Участий')}</th><th>{t('Куда')}</th></tr></thead>
          <tbody>
            {Object.entries(plan.by_step).map(([s, p]) => <tr key={s}><td><code>{s}</code></td><td>{num(p.count)}</td><td>{p.mapped_to ? <code>{p.mapped_to}</code> : <span className="muted">{t('остаётся на v{v}', { v: fromVersion })}</span>}</td></tr>)}
          </tbody>
        </table>
      )}
    </div>
  );
}
